import { useCalendarContext } from "@/features/calendar/CalendarProvider";
import { type OpenModalProps } from "@/features/calendar/openCalendarModal";
import { type CoWorkerCalendarItem } from "@/features/calendar/types";
import {
  getColor,
  getIsDashed,
  getRenderTypeInfoFromCalendarItem,
} from "@/features/calendar/utils";
import { useSettings } from "@repo/ui";
import { hasItems } from "@repo/utils";
import { differenceInDays } from "date-fns";

import { DraggableAppointmentBlockContainer } from "../components/DraggableAppointmentBlockContainer";
import { ShortWeekCalendarAppointment } from "./ShortWeekCalendarAppointment";
import { AppointmentBlock } from "./components";
import { WEEK_BLOCK_HEIGHT, WEEK_DATE_TITLE_HEIGHT } from "./constants";
import { getLeftPos, populateAllDayMap } from "./utils";

export const AllDayBlocks = ({
  items,
  startDate,
  blockWidth,
  openModal,
  showInitials,
}: {
  items: CoWorkerCalendarItem[];
  startDate: Date;
  blockWidth: number;
  openModal: (props: OpenModalProps) => void;
  showInitials?: boolean;
}) => {
  const { task_configuration, calendar } = useSettings();
  const { isItemSelected } = useCalendarContext();

  const blocks = [];

  if (!hasItems(items)) {
    return null;
  }

  const dayMap = populateAllDayMap(items);

  let i = 0;
  for (const item of items) {
    const itemIndex = dayMap[item.id ?? 0];
    const height = (15 * WEEK_BLOCK_HEIGHT) / 60;
    const left = getLeftPos(startDate, item.start, blockWidth);
    const endDateOffset = differenceInDays(item.end, item.start);
    const width =
      blockWidth + // 1 day
      endDateOffset * blockWidth; // Add width for number of days
    const top = WEEK_DATE_TITLE_HEIGHT + 1 + itemIndex * height;

    const renderinfo = getRenderTypeInfoFromCalendarItem(item);
    const colorInfo = getColor(
      renderinfo,
      task_configuration?.task_types,
      calendar?.activity_configuration
    );

    blocks.push(
      <DraggableAppointmentBlockContainer
        key={item.id}
        id={item.id}
        item={item}
        top={top}
        left={left}
        width={width}
        height={height}
        verticalMode
        onClick={() => {
          if (item.appointmentType === "outlook") {
            openModal({ type: "outlook", item });
          } else if (item.id) {
            openModal({ type: "task", id: item.id });
          }
        }}
      >
        <AppointmentBlock
          key={`${item.start.getTime()}-${item.emailId}-${i++}`}
          backgroundColor={colorInfo.color}
          borderColor={colorInfo.borderColor}
          dashed={getIsDashed(renderinfo)}
          selected={isItemSelected(item.id)}
        >
          <ShortWeekCalendarAppointment
            item={item}
            overlapping={true}
            showInitials={showInitials}
          />
        </AppointmentBlock>
      </DraggableAppointmentBlockContainer>
    );
  }
  return <>{blocks}</>;
};
